import { useState } from 'react';
import clsx from 'clsx';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { MODELO_EMAIL_CLIENTE_SUGERIDO, MOMENTO_EMAIL_CLIENTE_LABELS } from '../../../models/emailCliente.modelos';
import { useModeloEmailClienteMutations, useModelosEmailClienteQuery } from '../../../hooks/data';
import EmailClienteModeloEditor from './EmailClienteModeloEditor';

interface EmailsClienteConfigProps {
  onVoltar: () => void;
}

const NOVO = 'novo';

/**
 * Configurações → E-mails para o Cliente: pedido de usuário/máquina (entrada) e aviso de saída
 * que o SI manda ao Cliente (ex.: SWAP). Cada modelo é de um Cliente e aparece na Solicitação
 * dos tipos marcados — ver EmailsClienteSection (detalhe) e DadosClienteAbertura (abertura).
 */
export default function EmailsClienteConfig({ onVoltar }: EmailsClienteConfigProps) {
  const { data: modelos = [], isLoading } = useModelosEmailClienteQuery();
  const { salvar, excluir } = useModeloEmailClienteMutations();
  const [selecionado, setSelecionado] = useState<string>(NOVO);
  const atual = modelos.find((m) => m.id === selecionado) ?? null;

  return (
    <div className="space-y-4">
      <button type="button" onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Voltar
      </button>
      <div>
        <h3 className="font-montserrat text-lg font-semibold text-gray-900">E-mails para o Cliente</h3>
        <p className="text-sm text-gray-500">
          Pedido de acesso/máquina e aviso de saída enviados ao Cliente. Na Solicitação, o SI confere os dados e abre o e-mail
          pronto no Outlook.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {modelos.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setSelecionado(m.id)}
            className={clsx(
              'rounded-full px-3 py-1.5 text-xs transition-colors',
              selecionado === m.id ? 'bg-primary-600 font-semibold text-white' : 'border border-gray-200 text-gray-700 hover:bg-primary-50'
            )}
          >
            {m.clienteNome} · {MOMENTO_EMAIL_CLIENTE_LABELS[m.momento]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setSelecionado(NOVO)}
          className={clsx(
            'inline-flex items-center gap-1 rounded-full border border-dashed px-3 py-1.5 text-xs font-semibold',
            selecionado === NOVO ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-primary-50'
          )}
        >
          <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" /> Novo modelo
        </button>
      </div>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-xl bg-gray-100" />
      ) : atual ? (
        <EmailClienteModeloEditor
          key={JSON.stringify(atual)}
          inicial={atual}
          salvando={salvar.isPending}
          onSalvar={(modelo) => salvar.mutate({ ...modelo, id: atual.id })}
          onExcluir={() => {
            if (!window.confirm(`Excluir o modelo "${atual.clienteNome} · ${MOMENTO_EMAIL_CLIENTE_LABELS[atual.momento]}"?`)) return;
            excluir.mutate(atual.id, { onSuccess: () => setSelecionado(NOVO) });
          }}
        />
      ) : (
        <EmailClienteModeloEditor
          key={NOVO}
          inicial={{ clienteId: '', clienteNome: '', momento: 'entrada', para: [], cc: [], ...MODELO_EMAIL_CLIENTE_SUGERIDO.entrada }}
          salvando={salvar.isPending}
          onSalvar={(modelo) => salvar.mutate(modelo, { onSuccess: (id) => setSelecionado(id) })}
        />
      )}
    </div>
  );
}
